/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import {SchemaListItemDTO} from '../../../../../models/Schema';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {AppSettings} from '../../../../../models/Constants';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '@siemens/ix-angular';
import {SearchCriteria} from '../../../../../models/SearchCriteria';
import {MasterChildStringEnum} from '../../../../../models/Passport';

@Component({
  selector: 'app-side-bar-search',
  templateUrl: './sidebar-search.component.html',
  styleUrls: ['./sidebar-search.component.scss'],
  standalone: false,
})
export class SideBarSearchComponent implements OnDestroy {
  readonly translate = inject(TranslateService);
  readonly toastService = inject(ToastService);

  @Input() schemas: SchemaListItemDTO[] = [];
  @Input() numberOfFoundPassports = 0;
  @Input()
  set initialSearchCriteria(value: SearchCriteria) {
    this.criteria = {...(value || {})};
    if (
      typeof this.criteria.passportStatus !== 'object' ||
      this.criteria.passportStatus === null
    ) {
      this.criteria.passportStatus = {};
    }
  }

  @Output() searchChange = new EventEmitter<SearchCriteria>();

  readonly MasterChildFlag = MasterChildStringEnum;
  criteria: SearchCriteria = {type: {}, passportStatus: {}};

  readonly currentLocale = signal(
    this.translate.currentLang ?? this.translate.defaultLang,
  );

  updateDateSelected = false;
  reloadUpdateHappened = false;
  createDateSelected = false;
  reloadCreateHappened = false;

  readonly destroy$ = new Subject<void>();
  readonly searchChange$ = new Subject<SearchCriteria>();

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(({lang}) => this.currentLocale.set(lang));

    this.searchChange$
      .pipe(
        debounceTime(AppSettings.SEARCH_BAR_INPUT_TO_REQUEST_WAIT_TIME_IN_MS),
        takeUntil(this.destroy$),
      )
      .subscribe((criteria) => {
        this.searchChange.emit(criteria);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCriteriaChange() {
    this.searchChange$.next(this.criteria);
  }

  onClearSearch(field: keyof SearchCriteria) {
    this.criteria[field] = null;
    this.onCriteriaChange();
  }

  onSchemaChange(value: CustomEvent<string | string[]>) {
    if (value.detail === 'notDefined') {
      this.criteria.schemaId = null;
    } else {
      this.criteria.schemaId = (value.detail as string) || undefined;
    }
    this.onCriteriaChange();
  }

  onFlagToggleTypeMaster(isChecked: boolean) {
    this.criteria.type = this.criteria.type || {};
    this.criteria.type.master = isChecked;
    this.onCriteriaChange();
  }

  onFlagToggleTypeChild(isChecked: boolean) {
    this.criteria.type = this.criteria.type || {};
    this.criteria.type.child = isChecked;
    this.onCriteriaChange();
  }

  clearCreatedDateRange() {
    this.criteria.createdAtFrom = null;
    this.criteria.createdAtTo = null;
    this.createDateSelected = false;
    this.reloadCreateHappened = true;
    setTimeout(() => {
      this.reloadCreateHappened = false;
    }, 0);
    this.onCriteriaChange();
  }

  clearUpdatedDateRange() {
    this.criteria.updatedAtFrom = null;
    this.criteria.updatedAtTo = null;
    this.updateDateSelected = false;
    this.reloadUpdateHappened = true;
    setTimeout(() => {
      this.reloadUpdateHappened = false;
    }, 0);
    this.onCriteriaChange();
  }

  async onCreatedDateRangeChange(event: Event) {
    const dateRange = await this.getRangeValues(event);
    if (dateRange) {
      this.createDateSelected = true;
      this.criteria.createdAtFrom = dateRange.fromDate;
      this.criteria.createdAtTo = dateRange.toDate;
      this.onCriteriaChange();
    }
  }

  async onUpdatedDateRangeChange(event: Event) {
    const dateRange = await this.getRangeValues(event);
    if (dateRange) {
      this.updateDateSelected = true;
      this.criteria.updatedAtFrom = dateRange.fromDate;
      this.criteria.updatedAtTo = dateRange.toDate;
      this.onCriteriaChange();
    }
  }

  async getRangeValues(
    event: Event,
  ): Promise<{fromDate: string; toDate: string} | undefined> {
    const customEvent = event as CustomEvent<{from: string; to: string}>;
    const from = customEvent.detail.from;
    let to = customEvent.detail.to;

    return new Promise((resolve) => {
      if (!from) {
        resolve(undefined);
      }
      if (!to) {
        to = from;
      }

      const fromDate = new Date(from).toISOString();
      const toDateRaw = new Date(to);
      toDateRaw.setDate(toDateRaw.getDate() + 1);
      const toDate = toDateRaw.toISOString();
      resolve({fromDate, toDate});
    });
  }

  get tomorrowDate(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate());
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  onFlagToggleStatusDraft(isChecked: boolean): void {
    this.criteria.passportStatus = this.criteria.passportStatus || {};
    this.criteria.passportStatus.draft = isChecked;
    this.onCriteriaChange();
  }

  onFlagToggleStatusDone(isChecked: boolean): void {
    this.criteria.passportStatus = this.criteria.passportStatus || {};
    this.criteria.passportStatus.done = isChecked;
    this.onCriteriaChange();
  }
}
